import PropTypes from 'prop-types'

// Internal components
import { ErrorMessage } from '@hookform/error-message'
import { useEffect, useState, useRef, useContext } from 'react'
import { useEffectOnce } from 'react-use'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'next-i18next'

import FormationBlock from '@/components/FormationBlock/FormationBlock'
import FormationOverlay from '@/components/FormationOverlay/FormationOverlay'
import FormError from '@/components/FormError/FormError'
import { UserContext } from '../../context/UserContext'

const emptyClass = ' not-empty'
const requiredHint = ' *'

const FormItemFormation = (props) => {
  const { item, onFormItemChange, onFormItemLoad, shouldValidateState } = props

  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )
  const requiredFormationErrorMessage = t('FORMITEM__ERROR_REQUIRED')
  const requiredLineupErrorMessage = t('FORMITEM__ERROR_REQUIRED_LINEUP')

  const { register, setValue, watch, errors, getValues, formState, trigger } = useFormContext()

  const [isLoaded, setIsLoaded] = useState(false)
  const [newInitialLineUp, setNewInitialLineUp] = useState([])
  const [selectedSpot, setSelectedSpot] = useState(null)
  const [showFormation, setShowFormation] = useState(false)
  const [selectedFormation, setSelectedFormation] = useState(item?.formationData?.[0]?.title)
  const [isFormationRequired, setIsFormationRequired] = useState({ required: false })
  const [isLineupRequired, setIsLineupRequired] = useState({ required: false })

  const itemProps = item
  const itemRef = useRef()
  const itemValue = watch(item.name)
  const itemInputRef = register(item.name, isFormationRequired)

  const inputFieldRegs = []
  const inputFieldRefs = []
  item.playerList.forEach((inputField, index) => {
    inputFieldRegs[index] = register(inputField.name, isLineupRequired)
    inputFieldRefs[index] = useRef(null)
  })

  const playerArr = []
  for (let i = 0; i < item.teamSize; i++) {
    playerArr.push({ name: '', uuid: '', icon: 'add' })
  }

  const filterFormationTitle = (value, formationData) => {
    let formationTitle = ''
    formationData.forEach((option) => {
      formationTitle = option.id === value ? option.title : formationTitle
    })
    return formationTitle
  }

  useEffectOnce(() => {
    setNewInitialLineUp(playerArr)
  }, [])

  useEffect(() => {
    // Something going wrong here on FMF.
    // if (!item.revealed && item.required) {
    //   setIsFormationRequired({ required: requiredFormationErrorMessage })
    //   setIsLineupRequired({ required: requiredLineupErrorMessage })
    // }
  }, [])

  useEffect(() => {
    if (!shouldValidateState) {
      setIsFormationRequired({ required: false })
      setIsLineupRequired({ required: false })
    }
  }, [shouldValidateState, itemValue])

  useEffect(() => {
    item.playerList.forEach((input, index) => {
      const playerValue =
        newInitialLineUp?.[index]?.name && newInitialLineUp?.[index]?.uuid
          ? `${newInitialLineUp?.[index]?.name}::${newInitialLineUp?.[index]?.uuid}`
          : ''
      const currValue = getValues(input.name)
      if (currValue !== playerValue) setValue(input.name, playerValue)
    })
    trigger()
  }, [newInitialLineUp])

  useEffect(() => {
    if (!itemValue) return false
    if (itemValue === 'none') {
      setIsLineupRequired({ required: false })
      setNewInitialLineUp(playerArr)
    } else {
      setIsLineupRequired({ required: requiredLineupErrorMessage })
    }
    trigger()
    setShowFormation(itemValue !== '' && itemValue !== 'none')
    setSelectedFormation(filterFormationTitle(itemValue, item.formationData))
    if (!isLoaded) {
      onFormItemLoad({ itemProps, itemRef, itemValue })
      setIsLoaded(true)
    } else {
      onFormItemChange({ itemProps, itemRef, itemValue })
    }
  }, [itemValue])

  // Set the players back into the formation after moving modals
  useEffect(() => {
    if (!item.value || item.value === '') return
    setValue(item.name, item.value)
    item.playerList.forEach((input, index) => {
      // Check for the correct data model.
      if (input?.value?.split('::')[1]) {
        playerArr[index].name = input.value.split('::')[0]
        playerArr[index].uuid = input.value.split('::')[1]
        playerArr[index].icon = input.value === '' ? 'add' : 'change'
        playerArr[index].inField = input.value !== '::'
      } else {
        // Make sure it doesn't loop over an empty react hook form field
        if (input.type) return
        playerArr[index].name = input.name
        playerArr[index].uuid = input.uuid
        playerArr[index].icon = 'change'
        playerArr[index].inField = true
      }
    })
    setNewInitialLineUp(playerArr)
  }, [item.value])

  return (
    <>
      <div ref={itemRef} className={`FormItem FormItem--select ${itemValue ? emptyClass : ''}`}>
        <label className="FormItem-label" htmlFor={item.name}>{`${item.transl ? t(item.transl) : item.label}${
          item.required ? requiredHint : ''
        }`}</label>
        <select
          id="matchFormation"
          name="matchFormation"
          className="FormItem-input FormItem-input--select"
          defaultValue={item.value || ''}
          onChange={(e) => {
            itemInputRef.onChange(e)
          }}
          ref={itemInputRef.ref}
        >
          <option value="" disabled={true} key={`FormItem-option--1`}>{`${item.transl ? t(item.transl) : item.label}${
            item.required ? requiredHint : ''
          }`}</option>
          <option value={'none'} key={`FormItem-option--none`}>
            {t('FORMATION_UNSURE')}
          </option>
          {item.formationData.length &&
            item.formationData.map((option, index) => {
              return (
                <option value={option.id} disabled={option.disabled} key={`FormItem-option--${index}`}>
                  {option.title}
                </option>
              )
            })}
        </select>
      </div>

      <div className={`FormItem FormItem--formation ${!showFormation ? 'is-hidden' : ''}`}>
        <div className="FormItem-formationWrapper">
          <FormationBlock players={newInitialLineUp} formation={selectedFormation} setSelectedSpot={setSelectedSpot} />
          <FormationOverlay
            selectedSpot={selectedSpot}
            setSelectedSpot={setSelectedSpot}
            newInitialLineUp={newInitialLineUp}
            setNewInitialLineUp={setNewInitialLineUp}
            availablePlayers={item.availablePlayers}
          />
        </div>
        {/* Check if the playerlist contains a type for the data model structure. */}
        {item.playerList.map((formationInput, index) => {
          return (
            <div className="FormItem-formationError" key={`formationPlayer_${index}`}>
              <input
                // type={formationInput.type || 'hidden'}
                type={'text'}
                id={formationInput.name}
                name={formationInput.name}
                defaultValue={formationInput.value ? formationInput.value : ''}
                className="FormItem-input"
                ref={(e) => {
                  inputFieldRegs[index].ref(e)
                  inputFieldRefs[index].current = e
                }}
                {...inputFieldRegs[index]}
              />
              {formState.isSubmitted && (
                <ErrorMessage
                  key={`formationPlayerError_${index}`}
                  errors={errors}
                  name={formationInput.name}
                  as={<FormError />}
                />
              )}
            </div>
          )
        })}
      </div>

      {formState.isSubmitted && <ErrorMessage errors={errors} name={item.name} as={<FormError />} />}
    </>
  )
}

FormItemFormation.propTypes = {
  item: PropTypes.object,
  onFormItemChange: PropTypes.func,
  onFormItemLoad: PropTypes.func,
  shouldValidateState: PropTypes.bool
}

export default FormItemFormation
